import { Box, Typography } from '@mui/material';
import { memoize, sortBy } from 'lodash-es';
import React, { FC } from 'react';

import { CountryFlag } from 'shared/components/elements/CountryFlag';
import {
  LabeledPropertyType,
  LocationLabel,
  RecordType,
  RecordValue,
  spliceProperty,
} from 'shared/components/internal/Records';
import { type EntityTypeFieldsAndIndicator } from 'shared/components/internal/Records/useEntityTypeRecordFields';

import {
  DEVICE_PREFIX,
  DST_PREFIX,
  hasValue,
  SRC_PREFIX,
} from '../RecordOverview';
import { RecordProperty } from '../RecordProperty';

const locationProps = {
  city: 'ip_city',
  countryCode: 'ip_countryCode',
  countryName: 'ip_countryName',
  ip: 'ip',
  latitude: 'ip_latitude',
  longitude: 'ip_longitude',
  region: 'ip_region',
  intValue: 'ip_ipv4IntValue',
  version: 'ip_version',
};

const BLACKLIST = [locationProps.countryCode, locationProps.intValue];

export type AddressType = 'source' | 'destination' | 'device';

interface AddressProps {
  allRecordData: RecordType;
  data: RecordType;
  showEmpty: boolean;
  type: AddressType;
  entityTypeFieldsAndIndicator?: EntityTypeFieldsAndIndicator;
}

const getFullPropName = memoize((name: string, type: AddressType) => {
  const fullPropMap = {
    source: `${SRC_PREFIX}${name}`,
    destination: `${DST_PREFIX}${name}`,
    device: `${DEVICE_PREFIX}${name}`,
  };

  return fullPropMap.hasOwnProperty(type) ? fullPropMap[type] : name;
});

const parseData = memoize((origData: RecordType, prefix: string) => {
  const data: LabeledPropertyType[] = [];
  Object.entries(origData).map(([name, value]) =>
    data.push({
      name,
      label:
        name.indexOf(prefix) !== -1
          ? name.substring(prefix.length, name.length)
          : name,
      value,
    })
  );

  return data;
});

const HEADER_LABELS = {
  source: 'Source',
  destination: 'Destination',
  device: 'Device',
};

const DEVICE_TYPES = {
  source: 'srcDevice',
  destination: 'dstDevice',
  device: 'device',
};

export const Address: FC<React.PropsWithChildren<AddressProps>> = ({
  allRecordData,
  data,
  showEmpty,
  type,
  entityTypeFieldsAndIndicator,
}) => {
  const countryCode = data[locationProps.countryCode];
  const hasCountryCode = hasValue(countryCode);

  const parsedData = sortBy(parseData(data, 'ip_'), ({ label }) => label);

  const {
    matches: [ip, city, region, countryName, latitude, longitude],
    properties,
  } = spliceProperty(parsedData, [
    locationProps.ip,
    locationProps.city,
    locationProps.region,
    locationProps.countryName,
    locationProps.latitude,
    locationProps.longitude,
  ]);

  const contentProperties = properties
    .filter(
      ({ name, value }) =>
        !BLACKLIST.includes(name) && (showEmpty || hasValue(value))
    )
    .map(({ name, label, value }) => {
      return (
        <Box
          key={`address-prop-${name}`}
          sx={{ breakInside: 'avoid-column', wordBreak: 'break-all' }}
        >
          <RecordProperty
            label={label}
            name={getFullPropName(name, type)}
            record={allRecordData}
            entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
          >
            {`${value}`}
          </RecordProperty>
        </Box>
      );
    });

  const ipFieldHasIndicator =
    entityTypeFieldsAndIndicator?.entityTypeFields?.find(
      (field) => field.field === getFullPropName(locationProps.ip, type)
    );

  return (
    <Box
      flex='1'
      minHeight={hasCountryCode ? '200px' : undefined}
      position='relative'
    >
      <Box marginBottom='20px'>
        <Typography variant='h2' mb={1}>
          {HEADER_LABELS[type]}
          {!!ip && (
            <Box component='span'>
              :&nbsp;
              <Box
                component={RecordValue}
                display='inline-block'
                name={locationProps.ip}
                record={allRecordData}
                entityIndicatorData={
                  ipFieldHasIndicator
                    ? entityTypeFieldsAndIndicator?.entityIndicatorData
                    : undefined
                }
              >
                {ip.value}
              </Box>
            </Box>
          )}
        </Typography>
        <LocationLabel
          data={{
            city,
            region,
            countryName,
            latitude,
            longitude,
            type: DEVICE_TYPES[type] ?? 'device',
          }}
          showIcon={true}
        />
      </Box>
      {!!contentProperties && (
        <Box data-sumo-test='hello' sx={{ columnWidth: '200px' }}>
          {contentProperties}
        </Box>
      )}
      {hasCountryCode && (
        <Box
          left='calc(50% - 200px)'
          position='absolute'
          sx={{ opacity: 0.05 }}
          top='calc(50% - 100px)'
          zIndex='-1'
        >
          <CountryFlag countryCode={countryCode} size='xlarge' />
        </Box>
      )}
    </Box>
  );
};
